'use client'

import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useState, type FormEvent } from 'react'
import { authClient } from '../../lib/auth-client'
import { AuthShell, authStyles as s } from '../_components/auth/AuthShell'
import { VerifyEmailStep } from '../_components/auth/VerifyEmailStep'

export default function SignInPage() {
  // Wrap in Suspense because `useSearchParams()` opts the page out of
  // static rendering otherwise.
  return (
    <Suspense fallback={<AuthShell title="Loading…">{null}</AuthShell>}>
      <SignInFlow />
    </Suspense>
  )
}

function SignInFlow() {
  const router = useRouter()
  const searchParams = useSearchParams()
  // Route through the post-login dispatcher (`/home`) which branches by
  // active-org type (vendor → dashboard, else projects). Forward an
  // explicit deep-link `next` so RequireAuth redirects still land right.
  const next = searchParams.get('next')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Set when sign-in is blocked on an unverified email. Better Auth
  // auto-sends a fresh OTP on that failed attempt, so we just switch to
  // the verify step (which also offers a manual resend).
  const [needsVerify, setNeedsVerify] = useState(false)

  function goHome() {
    router.push(next ? `/home?next=${encodeURIComponent(next)}` : '/home')
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    const result = await authClient.signIn.email({ email, password })
    setSubmitting(false)
    if (result.error) {
      const code = result.error.code
      if (code === 'EMAIL_NOT_VERIFIED' || result.error.status === 403) {
        setNeedsVerify(true)
        return
      }
      setError(result.error.message ?? 'Sign in failed.')
      return
    }
    goHome()
  }

  if (needsVerify) {
    return (
      <VerifyEmailStep
        email={email}
        password={password}
        onVerified={goHome}
        onBack={() => setNeedsVerify(false)}
        backLabel="Use a different account"
      />
    )
  }

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to pick up your Tinkerspace designs where you left off."
      footer={
        <p className={s.foot}>
          New here? <Link href="/sign-up">Create an account</Link>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className={s.form}>
        <div className={s.field}>
          <label className={s.label} htmlFor="si-email">
            Email
          </label>
          <input
            id="si-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
            className={s.input}
          />
        </div>
        <div className={s.field}>
          <label className={s.label} htmlFor="si-password">
            Password
          </label>
          <div className={s.inputWrap}>
            <input
              id="si-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="current-password"
              placeholder="Your password"
              className={s.input}
            />
            <button
              type="button"
              className={s.toggle}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <Link href="/forgot-password" style={{ alignSelf: 'flex-start', fontSize: 12.5 }}>
            Forgot password?
          </Link>
        </div>
        {error ? <p className={s.error}>{error}</p> : null}
        <button type="submit" disabled={submitting} className={s.button}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthShell>
  )
}
